import { useParams, Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { IMG_BASE, COUNTRY_CODES } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

export default function FlagPage() {
  const { code } = useParams<{ code: string }>();
  const lowerCode = code?.toLowerCase() || "";
  const countryName = COUNTRY_CODES[lowerCode] || code?.toUpperCase() || "Unknown";

  if (!code) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Missing country code</h1>
          <Button asChild><Link to="/"><ArrowLeft className="w-4 h-4" />Back to Home</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout breadcrumbs={[{ label: "Flags", to: "/" }, { label: countryName }]}>
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={`${IMG_BASE}/flag/${lowerCode}`}
              alt={`${countryName} flag`}
              className="w-48 sm:w-60 rounded-xl object-contain border shadow-sm"
              onError={(e) => { (e.target as HTMLElement).style.opacity = "0.3"; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">{countryName}</h1>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">Flag</Badge>
              <Badge variant="outline" className="uppercase">{lowerCode}</Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/flag/${lowerCode}" />`} />
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Country flag (SVG by default) -->\n<img src="${IMG_BASE}/flag/${lowerCode}" />\n\n<!-- Explicit format -->\n<img src="${IMG_BASE}/flag/${lowerCode}?format=png" />\n<img src="${IMG_BASE}/flag/${lowerCode}?format=svg" />`} />
      </section>

      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Browse Flags</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(COUNTRY_CODES).filter(([c]) => c !== lowerCode).map(([c, name]) => (
            <Link key={c} to={`/flag/${c}`} className="flex items-center gap-3 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all no-underline">
              <img src={`${IMG_BASE}/flag/${c}`} alt={name} className="w-8 h-5 rounded-sm object-cover" loading="lazy" />
              <span className="text-sm font-medium text-secondary-foreground truncate">{name}</span>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
